import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import GymCard from './components/GymCard';
import GymForm from './components/GymForm';

interface Gym {
  _id: string;
  name: string;
  code: string;
  phone: string;
  address: string;
  city: string;
  photoUrl: string;
  timezone: string;
  description?: string;
  facilities?: string[];
  operatingHours?: any[];
  coverPhoto?: string;
}

const ITEMS_PER_PAGE = 6;

const GymManager: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch all gyms
  const { data: gyms = [], isLoading, error } = useQuery({
    queryKey: ['gyms'],
    queryFn: async () => {
      const response = await apiClient.get('/gyms');
      return Array.isArray(response.data) ? response.data : response.data?.data || [];
    },
  });

  const handleCreate = () => {
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    queryClient.invalidateQueries({ queryKey: ['gyms'] });
  };

  const handleViewDetails = (gymId: string) => {
    navigate(`/gyms/${gymId}`);
  };

  const filteredGyms = gyms.filter((gym: Gym) =>
    gym.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gym.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gym.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalItems = filteredGyms.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedGyms = filteredGyms.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 4;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 1; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading gyms...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-400">Error loading gyms</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#23F47C] mb-2">Gym Management</h1>
          <p className="text-gray-400">Manage and monitor all registered fitness centers in your network</p>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Search by gym name, code or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-[#23F47C]"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#23F47C]"
          >
            <option>Status: Active</option>
            <option>Status: Inactive</option>
            <option>All</option>
          </select>

          <button
            onClick={handleCreate}
            className="flex items-center gap-2 bg-[#23F47C] text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all"
          >
            <Plus size={20} />
            Create New Gym
          </button>
        </div>

        {/* Gym Grid */}
        {filteredGyms.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-4">No gyms found</p>
            <button
              onClick={handleCreate}
              className="text-[#23F47C] hover:underline"
            >
              Create the first gym
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {paginatedGyms.map((gym: Gym) => (
                <GymCard
                  key={gym._id}
                  gym={gym}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between">
              <div className="text-gray-400 text-sm">
                Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} gyms
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft size={20} />
                </button>

                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                      currentPage === page
                        ? 'bg-[#23F47C] text-gray-900'
                        : 'border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C]'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <GymForm
          gym={null}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default GymManager;
